import { FC, useEffect, useMemo, useRef, useState } from 'react'
import { TabsContent } from '../../components/ui/tabs'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card'
import { Button } from '../../components/ui/button'
import { Badge } from '../../components/ui/badge'
import { useUserContext } from '../../hooks/use-user'
import { SubscriptionTypeEnum } from '../../lib/enums'
import { Crown, ExternalLink, Loader2, AlertCircle, Zap, CreditCard, CheckCircle, RefreshCw } from 'lucide-react'
import { toast } from 'sonner'
import { useLemonSqueezy } from '../../hooks/use-lemon-squeezy'
import { collection, getDocs, updateDoc } from 'firebase/firestore'
import { useFirestore, useAuth } from '../../hooks/use-firebase'
import type { LemonSqueezySubscription } from '../../lib/types'
import initializeLemonSqueezy from '../../lemonSqueezy'
import PlanComparisonDialog from '../../components/subscription/PlanComparisonDialog'
import UpgradeDowngradeDialog from '../../components/subscription/UpgradeDowngradeDialog'
import CancellationDialog from '../../components/subscription/CancellationDialog'

const SubscriptionSettingsTab: FC = () => {
  const { user, subscription } = useUserContext()
  const {
    fetchSubscription,
    loading,
    createCheckoutSession,
    changePlan,
    cancelUserSubscription,
    resumeUserSubscription
  } = useLemonSqueezy()
  const [lemonSqueezyData, setLemonSqueezyData] = useState<LemonSqueezySubscription | null>(null)
  const [loadingSubscription, setLoadingSubscription] = useState(false)
  const [updatingSubscription, setUpdatingSubscription] = useState(false)
  const [creatingCheckout, setCreatingCheckout] = useState(false)
  const [fetchError, setFetchError] = useState(false) // Prevent retry loops on error
  const hasFetchedRef = useRef(false) // Prevent duplicate fetches
  const db = useFirestore()
  const auth = useAuth()

  console.log({ env: process.env.NODE_ENV })

  // Dialog states
  const [showPlanComparison, setShowPlanComparison] = useState(false)
  const [showUpgradeDialog, setShowUpgradeDialog] = useState(false)
  const [showCancelDialog, setShowCancelDialog] = useState(false)
  const [targetTier, setTargetTier] = useState<SubscriptionTypeEnum | null>(null)

  // Check if we're in development mode
  const isDevelopment = useMemo(() => {
    return auth?.currentUser?.uid === process.env.REACT_APP_ADMIN_UID || window.location.hostname === 'localhost'
  }, [auth?.currentUser?.uid])

  // Fetch subscription details from Firestore
  useEffect(() => {
    const fetchSubscriptionDetails = async () => {
      // Use Firebase Auth UID directly instead of user object
      const currentUserId = auth.currentUser?.uid

      // Skip if we've already fetched successfully (prevents infinite loops)
      if (hasFetchedRef.current) {
        return
      }

      // Skip if we already had a fetch error (prevents infinite retry loops)
      if (fetchError) {
        return
      }

      if (!currentUserId) {
        return
      }

      // Mark as fetched immediately to prevent race conditions
      hasFetchedRef.current = true
      setLoadingSubscription(true)
      try {
        // Get subscription from Firestore
        const subscriptionsRef = collection(db, `users/${currentUserId}/subscriptions`)
        // Simplified query - just get all subscriptions and filter/sort in memory
        // This avoids needing a Firestore composite index
        const snapshot = await getDocs(subscriptionsRef)

        // Filter for active subscriptions and sort by createdAt in memory
        const activeSubscriptions = snapshot.docs
          .map(doc => ({ id: doc.id, ...doc.data() }))
          .filter((sub: any) => sub.active === true)
          .sort((a: any, b: any) => {
            const aTime = a.createdAt?.toMillis?.() || 0
            const bTime = b.createdAt?.toMillis?.() || 0
            return bTime - aTime // Descending order
          })

        if (activeSubscriptions.length > 0) {
          const subDoc = activeSubscriptions[0]
          const lemonsqueezyId = subDoc.id

          if (lemonsqueezyId) {
            // Fetch full details from Lemon Squeezy
            const lsData = await fetchSubscription(lemonsqueezyId)
            if (lsData) {
              setLemonSqueezyData({ ...lsData, id: lemonsqueezyId })
            } else {
              // fetchSubscription returns null on error - prevent retry loops
              setFetchError(true)
            }
          }
        }
      } catch (error) {
        console.error('Error fetching subscription details:', error)
        toast.error('Failed to load subscription details')
        setFetchError(true) // Prevent retry loops
      } finally {
        setLoadingSubscription(false)
      }
    }

    fetchSubscriptionDetails()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.currentUser?.uid])

  const handleUpgrade = () => {
    setShowPlanComparison(true)
  }

  const handleManageSubscription = () => {
    if (lemonSqueezyData?.urls?.customer_portal) {
      window.open(lemonSqueezyData.urls.customer_portal, '_blank')
    } else {
      toast.error('Unable to open customer portal. Please contact support.')
    }
  }

  const handleSelectPlan = (tier: SubscriptionTypeEnum) => {
    setTargetTier(tier)
    setShowPlanComparison(false)
    setShowUpgradeDialog(true)
  }

  const handleConfirmPlanChange = async () => {
    if (!targetTier || !lemonSqueezyData?.id) {
      toast.error('Missing required information')
      return
    }

    const variantIdMap = {
      [SubscriptionTypeEnum.Free]: undefined, // Free tier has no variant ID
      [SubscriptionTypeEnum.Basic]: process.env.REACT_APP_LEMON_SQUEEZY_BASIC_VARIANT_ID,
      [SubscriptionTypeEnum.Premium]: process.env.REACT_APP_LEMON_SQUEEZY_PREMIUM_VARIANT_ID,
      [SubscriptionTypeEnum.Enterprise]: process.env.REACT_APP_LEMON_SQUEEZY_ENTERPRISE_VARIANT_ID,
    }

    const newVariantId = variantIdMap[targetTier]
    if (!newVariantId) {
      toast.error('Invalid plan selected')
      return
    }

    try {
      const result = await changePlan(lemonSqueezyData.id, parseInt(newVariantId))
      if (result) {
        setLemonSqueezyData(result)

        setShowUpgradeDialog(false)
        toast.success('Your plan has been updated! Waiting for changes to sync...')

        // Poll for custom claims update
        let attempts = 0
        const maxAttempts = 30 // 30 seconds max
        const pollInterval = 1000 // Check every second

        const checkCustomClaims = async (): Promise<boolean> => {
          if (!auth.currentUser) return false

          try {
            await auth.currentUser.getIdToken(true)
            const claims = await auth.currentUser.getIdTokenResult(true)
            const claimTier = claims.claims.subscription as SubscriptionTypeEnum

            return claimTier === targetTier
          } catch {
            return false
          }
        }

        const pollForUpdate = async () => {
          while (attempts < maxAttempts) {
            attempts++
            const isUpdated = await checkCustomClaims()

            if (isUpdated) {
              toast.success('Plan change complete! Refreshing...')
              setTimeout(() => {
                window.location.reload()
              }, 500)
              return
            }

            // Wait before next check
            await new Promise(resolve => setTimeout(resolve, pollInterval))
          }

          // Timeout - reload anyway
          toast.info('Taking longer than expected. Refreshing now...')
          setTimeout(() => {
            window.location.reload()
          }, 500)
        }

        pollForUpdate()
      }
    } catch {
      toast.error('Failed to change plan. Please try again.')
    }
  }

  const handleCancelSubscription = async (reason: string) => {
    if (!lemonSqueezyData?.id) {
      toast.error('No active subscription found')
      return
    }

    try {
      const result = await cancelUserSubscription(lemonSqueezyData.id)
      if (result) {
        setLemonSqueezyData(result)
        setShowCancelDialog(false)
        toast.success('Subscription cancelled. You will have access until the end of your billing period.')
      }
    } catch {
      toast.error('Failed to cancel subscription. Please try again.')
    }
  }

  const handleReactivateSubscription = async () => {
    if (!lemonSqueezyData?.id) {
      toast.error('No subscription found')
      return
    }

    setUpdatingSubscription(true)
    try {
      const result = await resumeUserSubscription(lemonSqueezyData.id)
      if (result) {
        setLemonSqueezyData(result)
        toast.success('Subscription reactivated successfully!')

        // Force token refresh
        if (auth.currentUser) {
          await auth.currentUser.getIdToken(true)
        }

        setTimeout(() => {
          window.location.reload()
        }, 1500)
      }
    } catch {
      toast.error('Failed to reactivate subscription. Please try again.')
    } finally {
      setUpdatingSubscription(false)
    }
  }

  const handlePauseInstead = () => {
    // Close cancellation dialog and show info
    setShowCancelDialog(false)
    toast.info('Pause feature coming soon! For now, please use the customer portal.')
    if (lemonSqueezyData?.urls?.customer_portal) {
      window.open(lemonSqueezyData.urls.customer_portal, '_blank')
    }
  }

  const handleTestSubscriptionUpdate = async (tier: 'basic' | 'premium' | 'enterprise') => {
    if (!auth.currentUser) {
      toast.error('User not authenticated')
      return
    }

    setUpdatingSubscription(true)
    try {
      // Get the auth token and UID directly from Firebase Auth
      const token = await auth.currentUser.getIdToken()
      const uid = auth.currentUser.uid

      // Call the cloud function
      const functionUrl = `https://us-central1-${process.env.REACT_APP_PROJECT_ID}.cloudfunctions.net/updateUserSubscription`

      const response = await fetch(functionUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          uid: uid,
          tier: tier,
          durationMonths: 1
        })
      })

      const data = await response.json()

      if (response.ok && data.success) {
        toast.success(`Subscription updated to ${tier}! Refreshing...`)

        // Force token refresh to get new custom claims
        await auth.currentUser.getIdToken(true)

        // Reload the page to apply new subscription
        setTimeout(() => {
          window.location.reload()
        }, 1500)
      } else {
        toast.error(data.error || 'Failed to update subscription')
      }
    } catch {
      toast.error('Failed to update subscription.')
    } finally {
      setUpdatingSubscription(false)
    }
  }

  const handleTestCheckout = async (tierName: 'Basic' | 'Premium' | 'Enterprise') => {
    if (!auth.currentUser?.email || !auth.currentUser) {
      toast.error('User not authenticated')
      return
    }

    const storeId = process.env.REACT_APP_LEMON_SQUEEZY_STORE_ID
    const variantId = tierName === 'Basic'
      ? process.env.REACT_APP_LEMON_SQUEEZY_BASIC_VARIANT_ID
      : tierName === 'Premium'
        ? process.env.REACT_APP_LEMON_SQUEEZY_PREMIUM_VARIANT_ID
        : process.env.REACT_APP_LEMON_SQUEEZY_ENTERPRISE_VARIANT_ID

    if (!storeId || !variantId) {
      toast.error('Lemon Squeezy not configured. Check environment variables.')
      return
    }

    setCreatingCheckout(true)
    try {
      initializeLemonSqueezy()
    } catch {
      toast.error("Error initializing Lemon Squeezy")
    }
    try {
      const checkout = await createCheckoutSession(storeId, variantId, {
        checkoutData: {
          email: auth.currentUser.email,
          custom: {
            firebase_uid: auth.currentUser.uid
          }
        }
      })

      if (checkout?.checkoutUrl) {
        toast.success('Redirecting to test checkout...')
        // Redirect to Lemon Squeezy checkout (test mode)
        window.location.href = checkout.checkoutUrl
      } else {
        toast.error('Failed to create checkout session')
      }
    } catch {
      toast.error('Failed to create checkout.')
    } finally {
      setCreatingCheckout(false)
    }
  }

  const getTierInfo = () => {
    switch (subscription) {
      case SubscriptionTypeEnum.Free:
        return {
          name: 'Free (No Subscription)',
          color: 'bg-neutral-400',
          icon: null,
          description: 'Choose a plan to start using InventorySumo'
        }
      case SubscriptionTypeEnum.Basic:
        return {
          name: 'Basic',
          color: 'bg-neutral-500',
          icon: null,
          description: 'Basic inventory management features'
        }
      case SubscriptionTypeEnum.Premium:
        return {
          name: 'Premium',
          color: 'bg-sumoPrimary text-sumoNavy',
          icon: <Crown className="w-4 h-4" />,
          description: 'Full listing management and sales tracking'
        }
      case SubscriptionTypeEnum.Enterprise:
        return {
          name: 'Enterprise',
          color: 'bg-gradient-to-r from-sumoPrimary to-sumoAmber text-sumoNavy',
          icon: <Crown className="w-4 h-4" />,
          description: 'Advanced features and priority support'
        }
      default:
        return {
          name: 'None',
          color: 'bg-neutral-500',
          icon: null,
          description: 'No Active Subscription'
        }

    }
  }

  const tierInfo = getTierInfo()
  const isFree = subscription === SubscriptionTypeEnum.Free
  const isBasic = subscription === SubscriptionTypeEnum.Basic

  return (
    <TabsContent value="subscription_settings" className="space-y-6">
      {/* Current Plan Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {tierInfo.icon}
            Current Plan
          </CardTitle>
          <CardDescription>Manage your subscription and billing</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Badge className={tierInfo.color}>
                  {tierInfo.name}
                </Badge>
                {!isFree && lemonSqueezyData?.status && (
                  <Badge variant="outline" className="capitalize">
                    {lemonSqueezyData.statusFormatted}
                  </Badge>
                )}
              </div>
              <p className="text-sm text-neutral-600 dark:text-neutral-400">
                {tierInfo.description}
              </p>
            </div>
            <div className="flex gap-2">
              {isFree ? (
                <Button onClick={handleUpgrade} variant="primary">
                  <Crown className="w-4 h-4 mr-2" />
                  Choose a Plan
                </Button>
              ) : (
                <>
                  {lemonSqueezyData?.cancelled ? (
                    <Button
                      onClick={handleReactivateSubscription}
                      variant="primary"
                      disabled={updatingSubscription}
                    >
                      {updatingSubscription ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Reactivating...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Reactivate Subscription
                        </>
                      )}
                    </Button>
                  ) : (
                    <>
                      <Button
                        onClick={() => setShowPlanComparison(true)}
                        variant="primary"
                      >
                        Change Plan
                      </Button>
                      <Button
                        onClick={handleManageSubscription}
                        variant="outline"
                        disabled={!lemonSqueezyData?.urls?.customer_portal}
                      >
                        <ExternalLink className="w-4 h-4 mr-2" />
                        Customer Portal
                      </Button>
                    </>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Subscription Details */}
          {!isFree && auth.currentUser && (
            <div className="pt-4 border-t dark:border-neutral-700">
              {loadingSubscription ? (
                <div className="flex items-center justify-center py-4">
                  <Loader2 className="w-5 h-5 animate-spin text-neutral-500" />
                  <span className="ml-2 text-sm text-neutral-500">Loading subscription details...</span>
                </div>
              ) : lemonSqueezyData ? (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="text-neutral-500 dark:text-neutral-400">Product</p>
                    <p className="font-medium text-neutral-900 dark:text-neutral-100">
                      {lemonSqueezyData.productName}
                    </p>
                  </div>
                  <div>
                    <p className="text-neutral-500 dark:text-neutral-400">Variant</p>
                    <p className="font-medium text-neutral-900 dark:text-neutral-100">
                      {lemonSqueezyData.variantName}
                    </p>
                  </div>
                  {lemonSqueezyData.renewsAt && !lemonSqueezyData.cancelled && (
                    <div>
                      <p className="text-neutral-500 dark:text-neutral-400">Next Billing Date</p>
                      <p className="font-medium text-neutral-900 dark:text-neutral-100">
                        {new Date(lemonSqueezyData.renewsAt).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                  {lemonSqueezyData.endsAt && lemonSqueezyData.cancelled && (
                    <div>
                      <p className="text-neutral-500 dark:text-neutral-400">Expires On</p>
                      <p className="font-medium text-error500">
                        {new Date(lemonSqueezyData.endsAt).toLocaleDateString()}
                      </p>
                    </div>
                  )}
                  {lemonSqueezyData.cardBrand && lemonSqueezyData.cardLastFour && (
                    <div>
                      <p className="text-neutral-500 dark:text-neutral-400">Payment Method</p>
                      <p className="font-medium text-neutral-900 dark:text-neutral-100">
                        {lemonSqueezyData.cardBrand} •••• {lemonSqueezyData.cardLastFour}
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-start gap-2 text-sm text-warning500 bg-warning500/10 p-3 rounded">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium">Subscription details not available</p>
                    <p className="text-neutral-600 dark:text-neutral-400 mt-1">
                      Your {tierInfo.name} subscription is active. To see billing details, payment method, and manage your subscription,
                      please complete a checkout through Lemon Squeezy or use the customer portal link above.
                    </p>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Cancelled Subscription Warning */}
          {lemonSqueezyData?.cancelled && (
            <div className="flex items-start gap-2 text-sm text-error500 bg-error500/10 p-3 rounded">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Subscription Cancelled</p>
                <p className="text-neutral-600 dark:text-neutral-400 mt-1">
                  Your subscription will remain active until {lemonSqueezyData.endsAt ? new Date(lemonSqueezyData.endsAt).toLocaleDateString() : 'the end of the billing period'}.
                  You can reactivate anytime from the customer portal.
                </p>
              </div>
            </div>
          )}

          {/* Cancel Subscription Button */}
          {!isFree && !lemonSqueezyData?.cancelled && (
            <div className="pt-4 border-t dark:border-neutral-700">
              <Button
                onClick={() => setShowCancelDialog(true)}
                variant="outline"
                className="w-full text-error500 hover:bg-error500/10 hover:border-error500"
              >
                Cancel Subscription
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Features Card */}
      <Card>
        <CardHeader>
          <CardTitle>Your Plan Features</CardTitle>
          <CardDescription>What's included in your {tierInfo.name} plan</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            {isFree ? (
              <>
                <FeatureItem text="No features available" />
                <FeatureItem text="Choose a subscription plan to get started" />
              </>
            ) : isBasic ? (
              <>
                <FeatureItem text="Up to 200 inventory items" />
                <FeatureItem text="Inventory tracking" />
                <FeatureItem text="Listing management" />
                <FeatureItem text="Sales tracking" />
                <FeatureItem text="Earnings dashboard" />
                <FeatureItem text="Units & bins organization" />
                <FeatureItem text="Manual barcode entry" />
                <FeatureItem text="Community support" />
              </>
            ) : subscription === SubscriptionTypeEnum.Premium ? (
              <>
                <FeatureItem text="Everything in Basic" />
                <FeatureItem text="Up to 1000 inventory items" />
                <FeatureItem text="Barcode generation & printing" />
                <FeatureItem text="Barcode scanning (coming soon)" />
                <FeatureItem text="Export inventory CSV" />
                <FeatureItem text="Advanced reporting" />
                <FeatureItem text="Priority support" />
                <FeatureItem text="Enhanced analytics" />
              </>
            ) : (
              <>
                <FeatureItem text="Everything in Premium" />
                <FeatureItem text="Unlimited inventory items" />
                <FeatureItem text="Multi-user access" />
                <FeatureItem text="API access" />
                <FeatureItem text="Custom integrations" />
                <FeatureItem text="Additional stores ($5/store)" />
                <FeatureItem text="Custom CSV reporting" />
                <FeatureItem text="Dedicated support" />
                <FeatureItem text="Custom training" />
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Development Testing Panel */}
      {isDevelopment && (
        <>
          <Card className="border-2 border-warning500 bg-warning500/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-warning700 dark:text-warning400">
                <Zap className="w-5 h-5" />
                Manual Subscription Testing
              </CardTitle>
              <CardDescription>
                Quickly switch between subscription tiers for testing. Only visible in development mode.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center gap-2 p-3 bg-warning500/10 rounded-lg text-sm text-warning700 dark:text-warning300">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />
                  <p>
                    This uses the <code className="px-1 py-0.5 bg-warning500/20 rounded">updateUserSubscription</code> cloud function.
                    After switching, the page will reload to apply changes.
                  </p>
                </div>

                <div className="flex flex-wrap gap-3">
                  <Button
                    onClick={() => handleTestSubscriptionUpdate('basic')}
                    disabled={updatingSubscription || subscription === SubscriptionTypeEnum.Basic}
                    variant={subscription === SubscriptionTypeEnum.Basic ? 'outline' : 'secondary'}
                    className="flex-1 min-w-[120px]"
                  >
                    {updatingSubscription ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : subscription === SubscriptionTypeEnum.Basic ? (
                      <span className="mr-2">✓</span>
                    ) : null}
                    Switch to Basic
                  </Button>

                  <Button
                    onClick={() => handleTestSubscriptionUpdate('premium')}
                    disabled={updatingSubscription || subscription === SubscriptionTypeEnum.Premium}
                    variant={subscription === SubscriptionTypeEnum.Premium ? 'primary' : 'outline'}
                    className="flex-1 min-w-[120px]"
                  >
                    {updatingSubscription ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : subscription === SubscriptionTypeEnum.Premium ? (
                      <>
                        <Crown className="w-4 h-4 mr-2" />
                        <span className="mr-2">✓</span>
                      </>
                    ) : (
                      <Crown className="w-4 h-4 mr-2" />
                    )}
                    Switch to Premium
                  </Button>

                  <Button
                    onClick={() => handleTestSubscriptionUpdate('enterprise')}
                    disabled={updatingSubscription || subscription === SubscriptionTypeEnum.Enterprise}
                    variant={subscription === SubscriptionTypeEnum.Enterprise ? 'primary' : 'outline'}
                    className="flex-1 min-w-[120px]"
                  >
                    {updatingSubscription ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : subscription === SubscriptionTypeEnum.Enterprise ? (
                      <>
                        <Crown className="w-4 h-4 mr-2" />
                        <span className="mr-2">✓</span>
                      </>
                    ) : (
                      <Crown className="w-4 h-4 mr-2" />
                    )}
                    Switch to Enterprise
                  </Button>
                </div>

                <div className="text-xs text-neutral-500 dark:text-neutral-400">
                  <p><strong>Current Tier:</strong> {subscription}</p>
                  <p><strong>User ID:</strong> {auth.currentUser?.uid}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Lemon Squeezy Webhook Testing */}
          <Card className="border-2 border-success500 bg-success500/5">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-success700 dark:text-success400">
                <CreditCard className="w-5 h-5" />
                Lemon Squeezy Webhook Testing
              </CardTitle>
              <CardDescription>
                Test the full checkout flow with Lemon Squeezy in test mode and verify webhook integration.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-start gap-2 p-3 bg-success500/10 rounded-lg text-sm text-success700 dark:text-success300">
                  <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold mb-1">Test Mode Active</p>
                    <p className="text-success600 dark:text-success400">
                      This will create a test checkout session. Use card <code className="px-1 py-0.5 bg-success500/20 rounded">4242 4242 4242 4242</code> to complete the purchase.
                      The webhook will process the subscription and update your account automatically.
                    </p>
                  </div>
                </div>

                <div>
                  <p className="text-sm font-medium text-neutral700 dark:text-neutral300 mb-3">
                    Choose a tier to test checkout:
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Button
                      onClick={() => handleTestCheckout('Basic')}
                      disabled={creatingCheckout}
                      variant="outline"
                      className="flex-1 min-w-[120px]"
                    >
                      {creatingCheckout ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : null}
                      Test Basic ($9.99)
                    </Button>

                    <Button
                      onClick={() => handleTestCheckout('Premium')}
                      disabled={creatingCheckout}
                      variant="primary"
                      className="flex-1 min-w-[120px]"
                    >
                      {creatingCheckout ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Crown className="w-4 h-4 mr-2" />
                      )}
                      Test Premium ($19.99)
                    </Button>

                    <Button
                      onClick={() => handleTestCheckout('Enterprise')}
                      disabled={creatingCheckout}
                      variant="outline"
                      className="flex-1 min-w-[120px]"
                    >
                      {creatingCheckout ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                      ) : (
                        <Crown className="w-4 h-4 mr-2" />
                      )}
                      Test Enterprise ($49.99)
                    </Button>
                  </div>
                </div>

                <div className="space-y-2 text-xs text-neutral-600 dark:text-neutral-400 pt-3 border-t dark:border-neutral700">
                  <p className="font-semibold">What happens next:</p>
                  <ol className="list-decimal list-inside space-y-1 ml-2">
                    <li>You'll be redirected to Lemon Squeezy checkout (test mode)</li>
                    <li>Complete purchase with test card: <code className="px-1 py-0.5 bg-neutral200 dark:bg-neutral700 rounded">4242 4242 4242 4242</code></li>
                    <li>Lemon Squeezy sends webhook to your cloud function</li>
                    <li>Webhook updates your Firebase Auth custom claims</li>
                    <li>Subscription document created in Firestore</li>
                    <li>You're redirected back to your app with active subscription!</li>
                  </ol>
                  <p className="text-warning600 dark:text-warning400 pt-2">
                    <strong>Monitor webhook:</strong> <code className="px-1 py-0.5 bg-warning500/20 rounded">firebase functions:log --only lemonsqueezyWebhook --follow</code>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Upgrade CTA for Free/Basic Users */}
      {(isFree || isBasic) && (
        <Card className="border-2 border-sumoPrimary bg-gradient-to-br from-sumoPrimary/5 to-sumoAmber/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Crown className="w-5 h-5 text-sumoPrimary" />
              {isFree ? 'Choose Your Plan' : 'Unlock Premium Features'}
            </CardTitle>
            <CardDescription>
              {isFree ? 'Subscribe to start using InventorySumo' : 'Get access to advanced features and grow your business'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={handleUpgrade} variant="primary" size="lg" className="w-full md:w-auto">
              {isFree ? 'Choose a Plan' : 'View Pricing Plans'}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Subscription Management Dialogs */}
      <PlanComparisonDialog
        open={showPlanComparison}
        onOpenChange={setShowPlanComparison}
        currentTier={subscription}
        onSelectPlan={handleSelectPlan}
      />

      {targetTier && (
        <UpgradeDowngradeDialog
          open={showUpgradeDialog}
          onOpenChange={setShowUpgradeDialog}
          currentTier={subscription}
          targetTier={targetTier}
          onConfirm={handleConfirmPlanChange}
          loading={loading}
        />
      )}

      <CancellationDialog
        open={showCancelDialog}
        onOpenChange={setShowCancelDialog}
        currentTier={subscription}
        renewsAt={lemonSqueezyData?.renewsAt || undefined}
        onConfirmCancel={handleCancelSubscription}
        onPauseInstead={handlePauseInstead}
        loading={loading}
      />
    </TabsContent>
  )
}

const FeatureItem: FC<{ text: string }> = ({ text }) => (
  <div className="flex items-center gap-2">
    <div className="w-1.5 h-1.5 rounded-full bg-sumoPrimary" />
    <span className="text-neutral-700 dark:text-neutral-300">{text}</span>
  </div>
)

export default SubscriptionSettingsTab
